import { useEffect } from 'react';
import type { PlatformBranding } from '../api/platform';

/**
 * Reads the tenant's branding off `current_client.branding` (from
 * /api/platform/me) and applies it as CSS custom properties on
 * <html> so the entire dashboard re-tints without per-component
 * wiring.
 *
 * Three mechanisms:
 *
 *  1. Inline style on <html> for every non-empty colour / radius /
 *     font field. Map keys mirror the platform-ui design tokens
 *     (--accent, --font-display, --radius-lg, etc.) so the tenant's
 *     value WINS the cascade against tokens.css.
 *
 *  2. <link rel="stylesheet"> tags injected into <head> for any
 *     Google Fonts referenced. URL comes pre-resolved from the
 *     server (font_display_url / font_body_url) so the dashboard
 *     doesn't need the catalogue bundled.
 *
 *  3. <style data-locksmith-tenant-css> injection for custom_css;
 *     scoped to the dashboard root via a wrapping selector so a
 *     tenant's CSS can't accidentally leak into other parts of the
 *     hosted shell (login, error pages).
 *
 * Cleanup on unmount + on branding change so swapping tenants
 * doesn't pile up stale style/link nodes.
 *
 * Rendered once at the top of App; takes no children. Lives in
 * theme/ because it's adjacent to ThemeContext: branding sits one
 * level "above" the dark/light token swap but uses the same
 * cascade-driven update model.
 */
export function BrandingApplier({ branding }: { branding: PlatformBranding | null }) {
  useEffect(() => {
    const root = document.documentElement;

    // ── Custom properties ─────────────────────────────────────
    const vars: Record<string, string> = {};
    if (branding) {
      // Colours map onto the platform-ui tokens.css tokens. Empty
      // values are skipped so the existing token defaults win.
      pushColor(vars, '--accent',         branding.primary_color);
      pushColor(vars, '--accent-hover',   branding.secondary_color);
      pushColor(vars, '--text',           branding.text_color);
      pushColor(vars, '--text-dim',       branding.text_dim_color);
      pushColor(vars, '--surface',        branding.surface_color);
      pushColor(vars, '--surface-2',      branding.surface_alt_color);
      pushColor(vars, '--border',         branding.border_color);

      // Fonts. font_display_url / font_body_url are blank for
      // platform fonts; for Google Fonts the server already
      // resolved the URL. The font-family declaration we set here
      // doesn't include the actual font name without the catalogue,
      // so the applier asks the consumer (the dashboard root) to
      // expose a class hook instead; see fontFamilyFor below.
      const display = fontFamilyFor(branding.font_display);
      const body    = fontFamilyFor(branding.font_body);
      if (display) vars['--font-display'] = display;
      if (body)    vars['--font-body']    = body;

      // Radii: 0 means "inherit product default", skip.
      if (branding.radius_sm) vars['--radius-sm'] = branding.radius_sm + 'px';
      if (branding.radius_md) vars['--radius-md'] = branding.radius_md + 'px';
      if (branding.radius_lg) vars['--radius-lg'] = branding.radius_lg + 'px';
    }
    // Track which vars we set so cleanup can unwind them precisely
    // (don't blanket-clear; ThemeContext + token defaults must
    // stay intact).
    const keys = Object.keys(vars);
    for (const k of keys) {
      const v = vars[k];
      if (v !== undefined) root.style.setProperty(k, v);
    }

    // ── Google Fonts links ────────────────────────────────────
    const links: HTMLLinkElement[] = [];
    function ensureFontLink(url: string, marker: string) {
      if (!url) return;
      let link = document.querySelector<HTMLLinkElement>(`link[data-locksmith-tenant-font="${marker}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.setAttribute('data-locksmith-tenant-font', marker);
        document.head.appendChild(link);
      }
      links.push(link);
    }
    if (branding?.font_display_url) {
      ensureFontLink(branding.font_display_url, 'display');
    }
    if (branding?.font_body_url) {
      ensureFontLink(branding.font_body_url, 'body');
    }

    // ── Custom CSS ────────────────────────────────────────────
    let styleEl: HTMLStyleElement | null = null;
    if (branding?.custom_css && branding.custom_css.trim()) {
      styleEl = document.createElement('style');
      styleEl.setAttribute('data-locksmith-tenant-css', '');
      // Wrap the tenant's CSS in a guard selector so rules at most
      // apply within the .app-shell tree, not the login or marketing
      // shell. Customers' selectors append onto this scope.
      styleEl.textContent =
        `.app-shell {\n${branding.custom_css}\n}`;
      document.head.appendChild(styleEl);
    }

    // ── Favicon ───────────────────────────────────────────────
    let prevFavicon: { el: HTMLLinkElement; href: string } | null = null;
    if (branding?.favicon_url) {
      const fav = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
      if (fav && fav.href !== branding.favicon_url) {
        prevFavicon = { el: fav, href: fav.href };
        fav.href = branding.favicon_url;
      }
    }

    return () => {
      // Reverse every change we made.
      for (const k of keys) root.style.removeProperty(k);
      for (const link of links) link.remove();
      if (styleEl) styleEl.remove();
      if (prevFavicon) prevFavicon.el.href = prevFavicon.href;
    };
  }, [
    // Re-apply whenever any addressable branding field changes.
    // Stringifying the whole object is cheap for this shape and
    // beats listing 17 deps by hand. Empty string when null so the
    // effect dep is always a string (TS doesn't allow undefined here).
    branding ? JSON.stringify(branding) : '',
  ]);

  return null;
}

/** Skip empty / falsy values so the existing token default wins.
 *  Hex validation already happened server-side. */
function pushColor(vars: Record<string, string>, key: string, value: string) {
  if (!value || !value.trim()) return;
  vars[key] = value.trim();
}

/** Translate a catalogue id ('inter', 'google:plus-jakarta', '') to
 *  a font-family declaration. Platform fonts get their full system
 *  stack; Google fonts get the family name with sensible fallbacks
 *  (sans-serif / serif / monospace inferred from the id when
 *  possible). Empty returns null so the existing var stays intact. */
function fontFamilyFor(id: string): string | null {
  if (!id) return null;
  if (!id.startsWith('google:')) {
    // Platform fonts: keep them aligned with services/font_catalogue
    // PLATFORM_FONTS.family. Hard-coded here to avoid bundling the
    // catalogue, which would force a separate fetch on first paint.
    switch (id) {
      case 'quicksand':
        return "'Quicksand', 'Helvetica Neue', Arial, sans-serif";
      case 'system-sans':
        return "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
      case 'system-serif':
        return "'Georgia', 'Times New Roman', Times, serif";
      case 'system-mono':
        return "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";
      default:
        return null;
    }
  }
  // Google: build a family name from the id, append a generic
  // fallback so the font doesn't go invisible while the <link>
  // resolves.
  const raw = id.slice('google:'.length);
  // Reverse the catalogue id format ("plus-jakarta" -> "Plus Jakarta")
  // by titlecasing words. Real labels (e.g. "DM Sans") come back
  // from the server as the link href so this is just for the
  // font-family declaration here; close enough for the cascade
  // to pick the right loaded font.
  const family = raw
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  const fallback = id.includes('mono')
    ? 'monospace'
    : id.includes('serif') || id.includes('lora') || id.includes('merriweather') || id.includes('playfair') || id.includes('crimson')
      ? 'serif'
      : 'sans-serif';
  return `'${family}', ${fallback}`;
}
